import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Minus, Plus, ShoppingBag, ChevronLeft, ChevronRight, Maximize2, Check } from "lucide-react";
import { toast } from "sonner";
import api, { resolveMedia } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { Lightbox } from "@/components/Lightbox";
import { CartSheet } from "@/components/shop/CartSheet";
import { AVAIL_LABELS, AVAIL_COLORS, availOf, isPurchasable } from "@/lib/shopUtils";
import { useStudio, getStudioKey } from "@/lib/studio";
import useSEO from "@/lib/useSEO";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n);
const RECENT_KEY = "gm_recent_products";

export default function ProductDetail() {
  const { id } = useParams();
  const { settings } = useSettings();
  const cart = useCart();
  const [p, setP] = useState(null);
  const [all, setAll] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);
  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const [lightbox, setLightbox] = useState(false);
  const [zoom, setZoom] = useState(false);
  const [added, setAdded] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const { studio, priceFor, active: studioActive, percent: studioPercent } = useStudio();

  // tracking Shop the Project: prodotto visualizzato
  useEffect(() => {
    const key = getStudioKey();
    if (key && p?.id) api.post(`/studio/${key}/track`, { event: "product_viewed", product_id: p.id }).catch(() => {});
  }, [p?.id]);

  useSEO({ title: p ? p.name : "Prodotto", description: p?.short_description || "Prodotto della selezione Giovanna Matino Interior." });

  useEffect(() => {
    setP(null); setImgIdx(0); setVariant(null); setQty(1); setNotFound(false);
    api.get(`/products/${id}`).then((r) => {
      setP(r.data);
      try {
        const rec = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]").filter((x) => x !== r.data.id);
        localStorage.setItem(RECENT_KEY, JSON.stringify([r.data.id, ...rec].slice(0, 6)));
      } catch { /* storage pieno o bloccato */ }
    }).catch(() => setNotFound(true));
    api.get("/products").then((r) => setAll(r.data)).catch(() => {});
  }, [id]);

  const gallery = useMemo(() => {
    if (!p) return [];
    const variantImg = variant?.image;
    const extra = (p.gallery || []).map((g) => (typeof g === "string" ? { url: g, alt: p.name } : g));
    const seen = new Set();
    const list = [...(variantImg ? [{ url: variantImg, alt: variant.name }] : []),
      { url: p.image, alt: p.name }, ...extra].filter((g) => g.url && !seen.has(g.url) && seen.add(g.url));
    return list;
  }, [p, variant]);

  const cur = useMemo(() => {
    if (!p) return null;
    return {
      price: variant?.price ?? p.price,
      sku: variant?.sku || p.sku || "",
      availability: variant?.availability || availOf(p),
      lead_time: variant?.lead_time || p.lead_time || p.lead_days || "",
    };
  }, [p, variant]);

  const related = useMemo(() => {
    if (!p) return [];
    const sameCollection = all.filter((x) => x.id !== p.id && p.collection && x.collection === p.collection);
    const sameCat = all.filter((x) => x.id !== p.id && x.id !== p.id &&
      (x.categories?.length ? x.categories : [x.category]).some((c) => (p.categories?.length ? p.categories : [p.category]).includes(c)));
    const merged = [...sameCollection, ...sameCat.filter((x) => !sameCollection.find((s) => s.id === x.id))];
    return merged.slice(0, 3);
  }, [p, all]);

  const recent = useMemo(() => {
    try {
      const ids = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]").filter((x) => x !== id);
      return ids.map((x) => all.find((a) => a.id === x)).filter(Boolean).slice(0, 3);
    } catch { return []; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, id, p]);

  if (notFound) {
    return (
      <div className="container-x py-24 text-center" data-testid="product-notfound">
        <h1 className="font-serif text-4xl">Prodotto non trovato</h1>
        <Link to="/shop" className="btn-ink mt-8 inline-flex">Torna allo Shop</Link>
      </div>
    );
  }
  if (!p) return <div className="container-x py-24 text-taupe">Caricamento…</div>;

  const purch = isPurchasable(p) && (!variant || !["esaurito", "temporaneamente_non_disponibile", "su_richiesta"].includes(variant.availability || "disponibile"));

  const doAdd = () => {
    cart.add(p, qty, variant);
    setAdded(true);
    toast.success(`${p.name}${variant ? ` (${variant.name})` : ""} aggiunto al carrello`, {
      action: { label: "Vai al carrello", onClick: () => setCartOpen(true) },
    });
    setTimeout(() => setAdded(false), 1600);
  };

  const requestPrice = () => {
    cart.add(p, qty, variant);
    window.location.href = "/shop?preventivo=1";
  };

  return (
    <div data-testid="page-product">
      <section className="container-x pt-8 pb-20">
        <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
          <nav className="text-xs text-taupe" data-testid="product-breadcrumb">
            <Link to="/shop" className="hover:text-ink">Shop</Link>
            <span className="mx-2">/</span>
            <span>{p.category}</span>
            <span className="mx-2">/</span>
            <span className="text-ink">{p.name}</span>
          </nav>
          <CartSheet open={cartOpen} onOpenChange={setCartOpen} testId="product-cart" />
        </div>

        <div className="grid lg:grid-cols-12 gap-10 items-start">
          {/* Galleria */}
          <div className="lg:col-span-7">
            <div className="relative bg-white border border-hair overflow-hidden group/gal">
              <button className="block w-full cursor-zoom-in" onClick={() => setLightbox(true)} data-testid="product-main-image-btn" aria-label="Ingrandisci immagine">
                <img src={resolveMedia(gallery[imgIdx]?.url || p.image)} alt={gallery[imgIdx]?.alt || p.name}
                  className={`w-full h-[380px] md:h-[560px] object-cover transition-all duration-500 ${zoom ? "scale-150" : ""}`}
                  onMouseEnter={() => setZoom(true)} onMouseLeave={() => setZoom(false)}
                  onMouseMove={(e) => {
                    const r = e.currentTarget.getBoundingClientRect();
                    e.currentTarget.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
                  }}
                  data-testid="product-main-image" />
              </button>
              <button onClick={() => setLightbox(true)} className="absolute top-3 right-3 bg-bone/90 p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-gold hover:text-bone transition-colors" data-testid="product-fullscreen" aria-label="A tutto schermo">
                <Maximize2 size={16} />
              </button>
              {gallery.length > 1 && (
                <>
                  <button onClick={() => setImgIdx((i) => (i - 1 + gallery.length) % gallery.length)} className="absolute left-3 top-1/2 -translate-y-1/2 bg-bone/90 p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-gold hover:text-bone transition-colors" data-testid="product-prev" aria-label="Precedente"><ChevronLeft size={16} /></button>
                  <button onClick={() => setImgIdx((i) => (i + 1) % gallery.length)} className="absolute right-3 top-1/2 -translate-y-1/2 bg-bone/90 p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-gold hover:text-bone transition-colors" data-testid="product-next" aria-label="Successiva"><ChevronRight size={16} /></button>
                </>
              )}
            </div>
            {gallery.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto pb-1" data-testid="product-thumbs">
                {gallery.map((g, i) => (
                  <button key={i} onClick={() => setImgIdx(i)} data-testid={`product-thumb-${i}`}
                    className={`w-20 h-20 shrink-0 border-2 overflow-hidden transition-colors ${i === imgIdx ? "border-gold" : "border-transparent hover:border-hair"}`}>
                    <img src={resolveMedia(g.url)} alt={g.alt || ""} className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info acquisto */}
          <div className="lg:col-span-5">
            <div className="eyebrow text-gold">{p.category}{p.collection ? ` · Collezione ${p.collection}` : ""}</div>
            <h1 className="font-serif text-4xl md:text-5xl mt-3 leading-tight" data-testid="product-title">{p.name}</h1>
            {cur.sku && <div className="text-[11px] tracking-[0.14em] uppercase text-taupe mt-2" data-testid="product-sku">Cod. {cur.sku}</div>}

            <div className="mt-5 flex items-baseline gap-3 flex-wrap">
              {p.price_on_request ? (
                <span className="font-serif text-2xl text-gold" data-testid="product-price">Prezzo esclusivo su richiesta</span>
              ) : (
                <>
                  {p.compare_price > cur.price && !studioActive && <span className="text-taupe line-through text-lg" data-testid="product-compare-price">{eur(p.compare_price)}</span>}
                  {(() => {
                    const reserved = priceFor(p, cur.price);
                    if (reserved !== null && reserved < cur.price) {
                      return (
                        <>
                          <span className="text-taupe line-through text-lg" data-testid="product-list-price">{eur(cur.price)}</span>
                          <span className="font-serif text-3xl text-gold" data-testid="product-studio-price">{eur(reserved)}</span>
                          <span className="text-[10px] tracking-[0.12em] uppercase border border-gold/50 text-gold px-2 py-1" data-testid="product-studio-badge">Prezzo Cliente Studio −{studioPercent}%</span>
                        </>
                      );
                    }
                    return <span className="font-serif text-3xl" data-testid="product-price">{eur(cur.price)}</span>;
                  })()}
                </>
              )}
            </div>

            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className={`inline-block text-[10px] tracking-[0.12em] uppercase border px-2 py-1 ${AVAIL_COLORS[cur.availability]}`} data-testid="product-avail">
                {AVAIL_LABELS[cur.availability]}
              </span>
              {cur.lead_time && <span className="text-xs text-taupe" data-testid="product-lead">Tempistica indicativa: {cur.lead_time}</span>}
              {p.lead_date && <span className="text-xs text-taupe">· indicativamente entro il {p.lead_date}</span>}
            </div>

            {p.short_description && <p className="mt-5 text-ink leading-relaxed" data-testid="product-short">{p.short_description}</p>}

            {/* Varianti */}
            {(p.variants || []).length > 0 && (
              <div className="mt-6" data-testid="product-variants">
                <div className="text-[10px] tracking-[0.14em] uppercase text-taupe mb-2">Variante</div>
                <div className="flex flex-wrap gap-2">
                  {(p.variants || []).map((v) => (
                    <button key={v.name} onClick={() => { setVariant(variant?.name === v.name ? null : v); setImgIdx(0); }}
                      data-testid={`product-variant-${v.name.replace(/\s+/g, "-")}`}
                      className={`px-4 py-2.5 min-h-[44px] text-sm border transition-colors ${variant?.name === v.name ? "bg-ink text-bone border-ink" : "border-hair hover:border-ink"}`}>
                      {v.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantità + CTA */}
            <div className="mt-7 flex items-center gap-3">
              <div className="flex items-center border border-hair">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-11 h-11 flex items-center justify-center hover:border-ink" data-testid="product-qty-dec" aria-label="Riduci quantità"><Minus size={14} /></button>
                <span className="w-10 text-center text-sm" data-testid="product-qty">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} className="w-11 h-11 flex items-center justify-center hover:border-ink" data-testid="product-qty-inc" aria-label="Aumenta quantità"><Plus size={14} /></button>
              </div>
              {p.price_on_request || !purch ? (
                <button onClick={requestPrice} className="btn-ink flex-1 min-h-[48px]" data-testid="product-request-price">
                  Richiedi prezzo esclusivo
                </button>
              ) : (
                <button onClick={doAdd} className={`btn-ink flex-1 min-h-[48px] inline-flex items-center justify-center gap-2 transition-colors ${added ? "!bg-emerald-700" : ""}`} data-testid="product-add-cart">
                  {added ? <><Check size={16} /> Aggiunto</> : <><ShoppingBag size={16} /> Aggiungi al carrello</>}
                </button>
              )}
            </div>
            {p.info_request !== false && !p.price_on_request && !purch && (
              <button onClick={requestPrice} className="mt-3 w-full min-h-[44px] text-xs tracking-[0.14em] uppercase border border-hair hover:border-ink transition-colors" data-testid="product-ask-info">
                Richiedi informazioni su questo prodotto
              </button>
            )}

            {/* Specifiche */}
            {(p.materials || p.colors || p.dimensions) && (
              <dl className="mt-8 border-t border-hair pt-6 space-y-2 text-sm" data-testid="product-specs">
                {p.materials && <div className="flex justify-between gap-4"><dt className="text-taupe">Materiali</dt><dd className="text-right">{p.materials}</dd></div>}
                {p.colors && <div className="flex justify-between gap-4"><dt className="text-taupe">Colori</dt><dd className="text-right">{p.colors}</dd></div>}
                {p.dimensions && <div className="flex justify-between gap-4"><dt className="text-taupe">Dimensioni</dt><dd className="text-right">{p.dimensions}</dd></div>}
              </dl>
            )}

            {p.description && <p className="mt-6 text-sm text-taupe leading-[1.9] font-light" data-testid="product-description">{p.description}</p>}

            <div className="mt-8 border border-hair bg-white p-5 space-y-3 text-xs text-taupe leading-relaxed" data-testid="product-shipping-info">
              <p><strong className="text-ink">Spedizione.</strong> {settings.shop_shipping_info}</p>
              <p><strong className="text-ink">Resi.</strong> {settings.shop_returns_info}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Correlati / stessa collezione */}
      {related.length > 0 && (
        <section className="bg-white border-t border-hair py-16" data-testid="product-related">
          <div className="container-x">
            <div className="eyebrow text-gold">{p.collection ? "Stessa collezione e affini" : "Potrebbero interessarti"}</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
              {related.map((r) => (
                <Link key={r.id} to={`/shop/${r.id}`} className="group" data-testid={`related-${r.id}`}>
                  <div className="overflow-hidden bg-bone"><img src={resolveMedia(r.image)} alt={r.name} loading="lazy" className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-700" /></div>
                  <div className="mt-3 font-serif text-xl group-hover:text-gold transition-colors">{r.name}</div>
                  <div className="text-sm text-taupe mt-1">{r.price_on_request ? "Prezzo su richiesta" : eur(r.price)}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Visti di recente */}
      {recent.length > 0 && (
        <section className="container-x py-16" data-testid="product-recent">
          <div className="eyebrow text-taupe">Visti di recente</div>
          <div className="grid grid-cols-3 gap-4 mt-5 max-w-2xl">
            {recent.map((r) => (
              <Link key={r.id} to={`/shop/${r.id}`} className="group" data-testid={`recent-${r.id}`}>
                <img src={resolveMedia(r.image)} alt={r.name} loading="lazy" className="w-full h-28 md:h-36 object-cover" />
                <div className="mt-2 text-xs truncate group-hover:text-gold transition-colors">{r.name}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {lightbox && (
        <Lightbox images={gallery} index={imgIdx} onClose={() => setLightbox(false)} onNavigate={setImgIdx} />
      )}
    </div>
  );
}
